/**
 *
 * SideNav.jsx
 *
 * This component renders the side navigation bar
 *
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import Logger from '../../utils/logger';
import LoadingSpinner from '../spinners/LoadingSpinner';
import getClientConfig from '../../utils/getClientConfig';
import moment from 'moment';
import LoggedInRouter from '../../components/routing/LoggedIn';
import { AuiSidenav } from '@avalara/skylab-sdk/react';
import AUTHENTICATION_CONTROLLER from '../../controllers/authentication';

const log = new Logger('SIDE NAV');

const SideNav = ({
  location,
  history,
  hideMenu,
  featureFlagsLoaded,
  featureFlags,
  dynamicMenuItems,
  iamData,
  displayLanguage,
  doLoadDynamicMenu
}) => {
  const [hasLoadedDynamicMenu, setHasLoadedDynamicMenu] = useState(false);

  /**
   * Checks if a URL is on the same domain as the current window.
   */
  const isRewrittenUrlSameDomain = useCallback((url) => {
    return url.startsWith(window.location.origin);
  }, []);

  /**
   * Dynamic Menu Rendering: If URL matches origin, return relative path, else absolute.
   */
  const getDynamicMenuURL = useCallback((url) => {
    return isRewrittenUrlSameDomain(url)
      ? url.replace(window.location.origin, '')
      : url;
  }, [isRewrittenUrlSameDomain]);

  /**
   * Constructs the Compliance Calendar URL with optional filing period parameters.
   */
  const getComplianceCalendarUrl = useCallback((url = '') => {
    const complianceCalendarUrl = url || getClientConfig().complianceCalendarUrl;

    if (!complianceCalendarUrl) return '#';

    if (!location.pathname.includes('openReturns')) {
      return complianceCalendarUrl;
    }

    const searchParams = new URLSearchParams(location.search);
    const defaultYear = searchParams.get('defaultYear');
    const defaultMonth = searchParams.get('defaultMonth');
    const year = searchParams.get('year');
    const month = searchParams.get('month');

    const currentDate = moment();
    let targetDate;

    if (defaultYear === 'now' && defaultMonth === 'now') {
      targetDate = currentDate;
    } else if (defaultYear === 'now' && month) {
      targetDate = moment(`${currentDate.format('YYYY')}-${month}-01`);
    } else if (year && month) {
      targetDate = moment(`${year}-${month}-01`);
    } else {
      targetDate = currentDate;
    }

    const nextMonth = targetDate.clone().add(1, 'month');
    const monthName = nextMonth.format('MMMM');
    const yearName = nextMonth.format('YYYY');

    return `${complianceCalendarUrl}?filingPeriod=${monthName}+${yearName}`;
  }, [location.pathname, location.search]);

  /**
   * Determines active menu based on current pathname.
   */
  const activeMenu = useMemo(() => {
    const pathname = location.pathname;

    if (pathname.startsWith('/DynamicReports/')) {
      return 'reports';
    }

    switch (pathname) {
      case '/Dashboard':
        return 'dashboard';
      case '/openReturns':
      case '/closedReturns':
        return 'returns';
      case '/errorsAndWarnings':
        return 'errors';
      case '/TransactionSearch':
      case '/upload':
      case '/newUpload':
      case '/ProcessedDocuments':
      case '/ManualDocuments':
      case '/BulkActions':
      case '/templates':
        return 'transactions';
      case '/TransactionReports':
      case '/VATCodeReport':
      case '/viewTransmissions':
      case '/MergeXMLfiles':
      case '/DynamicReports':
        return 'reports';
      case '/SalesAndPurchase':
        return 'ledgers';
      case '/AccountSettings':
      case '/Companies':
      case '/UserManagement':
      case '/CompanyPermissions':
      case '/VATCodes':
      case '/VATCodeSimulator':
      case '/ExchangeRates':
      case '/CountriesOnHold':
      case '/setupLiveReporting':
      case '/VATGroups':
        return 'settings';
      default:
        return 'returns';
    }
  }, [location.pathname]);

  /**
   * Handle Adobe Analytics tracking and dynamic menu loading.
   */
  useEffect(() => {
    // Adobe Analytics tracking
    if (window && window._satellite) {
      window._satellite.track('page_view');
    }
  }, [location.pathname]);

  useEffect(() => {
    // Dynamic menu loading logic
    if (
      !hasLoadedDynamicMenu &&
      featureFlagsLoaded &&
      featureFlags?.EnableDynamicMenu
    ) {
      // Load if dynamicMenuItems is null, undefined, or empty array
      if (!dynamicMenuItems || dynamicMenuItems.length === 0) {
        const enableSideNavigation = featureFlags?.EnableSideNavigation;
        doLoadDynamicMenu(enableSideNavigation);
        setHasLoadedDynamicMenu(true);
      }
    }
  }, [hasLoadedDynamicMenu, featureFlagsLoaded, featureFlags, dynamicMenuItems, doLoadDynamicMenu]);

  /**
   * Closes any open dropdowns.
   */
  const closeAnyOpenDropdowns = useCallback(() => {
    const openDropdowns = document.querySelectorAll('s-dropdown[open]');
    openDropdowns.forEach((dropdown) => dropdown.removeAttribute('open'));
  }, []);

  /**
   * Navigation handler for customer menu clicks.
   */
  const navHandlerForCustomer = useCallback((e, href) => {
    e.preventDefault();
    href = e.currentTarget.href;
    closeAnyOpenDropdowns();

    if (href.includes('support')) {
      window.open(href, '_blank').focus();
    } else if (href && isRewrittenUrlSameDomain(href)) {
      const relativePath = href.replace(window.location.origin, '');
      history.push(relativePath);
    } else if (href && href.startsWith('/')) {
      history.push(href);
    } else if (href && href.includes('http') && !isRewrittenUrlSameDomain(href)) {
      window.location.href = href;
    }
  }, [history, closeAnyOpenDropdowns, isRewrittenUrlSameDomain]);

  /**
   * Filters and transforms sub-items for the menu.
   */
  const filterAndTransformSubItems = useCallback((subItems, iamAuthEnabled) => {
    return subItems
      .filter((subItem) => {
        if (subItem.url.includes('CompanyPermissions')) {
          return iamAuthEnabled;
        }
        return true;
      })
      .map((subItem) => ({
        ...subItem,
        url: getDynamicMenuURL(subItem.url)
      }));
  }, [getDynamicMenuURL]);

  /**
   * Transforms a single menu item.
   */
  const transformMenuItem = useCallback((menu, iamAuthEnabled) => {
    const menuItem = {
      label: menu.label,
      icon: menu.icon,
      dataRef: menu.label.toLowerCase() === 'errors & warnings' ? 'errors' : menu.label.toLowerCase(),
    };

    if (menu.menuItems?.length > 0 && Array.isArray(menu.menuItems)) {
      menuItem.menuItems = filterAndTransformSubItems(menu.menuItems, iamAuthEnabled);
    } else {
      const isDashboard = (menu.label || '').toLowerCase() === 'dashboard';
      const computedUrl = isDashboard ? getComplianceCalendarUrl(menu.url) : menu.url;
      menuItem.url = computedUrl ? getDynamicMenuURL(computedUrl) : computedUrl;
    }

    return menuItem;
  }, [filterAndTransformSubItems, getComplianceCalendarUrl, getDynamicMenuURL]);

  /**
   * Gets navigation items for the sidenav.
   */
  const navItems = useMemo(() => {
    if (!dynamicMenuItems || dynamicMenuItems.length === 0) {
      return [];
    }

    const iamAuthEnabled = iamData?.isIamUser;
    return dynamicMenuItems.map((menu) => transformMenuItem(menu, iamAuthEnabled));
  }, [dynamicMenuItems, iamData, transformMenuItem]);

  /**
   * Determines if sidenav should be rendered.
   * Only render when navItems has items.
   */
  const shouldRenderSideNav = useMemo(() => {
    return (
      !hideMenu &&
      featureFlagsLoaded &&
      featureFlags?.EnableDynamicMenu &&
      featureFlags?.EnableSideNavigation &&
      navItems.length > 0
    );
  }, [hideMenu, featureFlagsLoaded, featureFlags, navItems]);

  log.debug('render(): ', { location, hideMenu, featureFlags });

  return (
    <div>
      <LoadingSpinner />
      {/* LoggedInRouter must keep one position in the tree: moving it between branches remounts the routed page. */}
      <div className={shouldRenderSideNav ? 'flex align-items-stretch' : undefined}>
        {shouldRenderSideNav && (
          <AuiSidenav
            data-testid="aui-sidenav"
            active={activeMenu}
            navItems={navItems}
            navHandler={(e, url) => navHandlerForCustomer(e, url)}
          />
        )}
        <LoggedInRouter displayLanguage={displayLanguage} />
      </div>
    </div>
  );
};

// Which props do we want to inject, given the global state?
function mapStateToProps(state) {
  log.debug('mapStateToProps: ', state);
  return {
    authentication: state.authentication,
    dynamicMenuItems: state.authentication && state.authentication.dynamicMenuItems,
    userName: state.authentication && state.authentication.data && state.authentication.data.userName,
    role: state.authentication && state.authentication.data && state.authentication.data.role,
    featureFlags: state.featureFlag && state.featureFlag.data,
    featureFlagsLoaded: state.featureFlag && state.featureFlag.loaded,
    iamData: state.authentication && state.authentication.data && state.authentication.data.iamData
  };
}

function mapDispatchToProps(dispatch) {
  return {
    doLoadDynamicMenu: (enableSideNavigation) => {
      log.debug('mapDispatchToProps => doLoadDynamicMenu(): ');
      dispatch(AUTHENTICATION_CONTROLLER.doLoadDynamicMenuItems(enableSideNavigation));
    }
  };
}


SideNav.defaultProps = {
  hideMenu: false
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(SideNav));
